/**
 * EditAssessmentInstructor Component
 * ORCA360 - Instructor Effectiveness Assessment Editor
 *
 * Allows reviewing and editing AI-generated instructor effectiveness assessments
 * Features: Audit logging, evidence validation, transcript viewer
 *
 * PARITY WITH PILOT EDITOR:
 * - ✅ Audit logging for compliance
 * - ✅ Split-screen transcript viewer
 * - ✅ Evidence add/edit/delete modals
 * - ✅ Development Areas Analysis field
 * - ✅ Professional Development Goals field
 * - ✅ Click-to-jump timestamp functionality
 * - ✅ Keyboard shortcuts and unsaved changes warning
 * - ✅ PDF export (usePDFExportInstructorAssessment)
 * - ✅ Word export (useWordExportInstructorAssessment)
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { describeFinalisationError, formatDateForFilename, overallResultForExport } from '@orca360/shared';
import { supabase } from '../lib/supabase';
import { useAuditLog } from '../hooks/useAuditLog';
import { useSessionAssessmentDraft } from '../hooks/useSessionAssessmentDraft';
import { getSessionSaveButtonLabel } from '../lib/session-assessment-draft';
import { SessionAssessmentUnsavedBanner } from '../components/SessionAssessmentUnsavedBanner';
import { createVersion } from '../lib/assessment-versions';
import { saveSessionBatch } from '../lib/save-session-assessment-batch';
import type { BatchAssessmentUpdate } from '../lib/assessment-versions';
import { usePDFExportInstructorAssessment } from '../hooks/usePDFExportInstructorAssessment';
import { useWordExportInstructorAssessment } from '../hooks/useWordExportInstructorAssessment';
import { EvidenceEditModal } from '../components/EvidenceEditModal';
import { LLMValidationPanel, type Evidence as LLMEvidence } from '../components/LLMValidationPanel';
import { AddEvidenceModal } from '../components/AddEvidenceModal';
import TranscriptViewer from '../components/TranscriptViewer';
import { VersionHistoryDrawer } from '../components/VersionHistoryDrawer';
import {
  mapAssessmentsToPartB,
  mapAssessmentsToPartC,
  mapCalibrationData,
  createDefaultPartF,
  createInstructorAssessmentExportData,
  type InstructorAssessmentExportData,
  type InstructorAssessmentPartA,
  type AssessmentInstructorRow,
} from '../types/instructor-assessment-export';

interface Evidence {
  quote: string;
  timestamp: string;
  validation_status: string;
  confidence?: number;
  // Tier 4 LLM Validation (optional - only present if LLM validation completed)
  llm_decision?: 'ACCEPTED' | 'REJECTED' | 'RECLASSIFIED';
  llm_reasoning?: string;
  reclassified_to_competency?: string;
}

/**
 * Stored development-level context, read from
 * assessments_instructor.development_level_context (renamed by migration 246).
 *
 * Rows written before HG-2B G2-05 carry the withdrawn key spellings and are
 * never rewritten, because finalized instructor assessments are frozen under
 * the 7-year retention rule (migration 239's standing decision). Both spellings
 * are therefore declared and both are read; nothing in this file writes the
 * column. Contract: docs/guide-change-spec/GUIDE-G2-05-RENAME-MAP.md v1.0 rows
 * 3 to 7. The API-side implementation of the same five fallbacks lives in
 * api/lib/development-level-context.ts; the duplication is deliberate and
 * explained there.
 */
interface DevelopmentLevelContext {
  traineeDevelopmentLevelAssumed?: string | null;
  /** @deprecated withdrawn spelling, present in rows written before G2-05 */
  traineeTierAssumed?: string | null;
  instructorTypeAssumed?: string | null;
  facilitationBalance?: {
    facilitationPercentage?: number;
    instructionPercentage?: number;
    observedFacilitationPercentage?: number;
    observedInstructionPercentage?: number;
    expectedFacilitationPercentage?: number;
    expectedInstructionPercentage?: number;
    developmentLevelAlignmentDelta?: number;
    developmentLevelAlignmentVerdict?: string;
    /** @deprecated withdrawn spelling, present in rows written before G2-05 */
    tierAlignmentDelta?: number;
    /** @deprecated withdrawn spelling, present in rows written before G2-05 */
    tierAlignmentVerdict?: string;
    assessment?: string;
  } | null;
  f3Assessment?: {
    fairness?: string;
    firmness?: string;
    friendliness?: string;
    comment?: string;
  } | null;
  developmentLevelAlignmentTags?: {
    strengths?: string[];
    areasForDevelopment?: string[];
  } | null;
  /** @deprecated withdrawn spelling, present in rows written before G2-05 */
  tierAlignmentTags?: {
    strengths?: string[];
    areasForDevelopment?: string[];
  } | null;
}

interface Assessment {
  id: string;
  session_id: string;
  instructor_id: string;
  criterion: string;
  score: number;
  evidence: Evidence[];
  analysis: string;
  goals: string;
  status: 'draft' | 'under_review' | 'approved' | 'published';
  version: number;
  created_at: string;
  updated_at: string;
  // Calibration fields (Migration 088)
  ai_evidence_aligned?: boolean | null;
  ai_grade_aligned?: boolean | null;
  ai_rationale_aligned?: boolean | null;
  ai_drift_detected?: 'Yes' | 'No' | 'Mild drift' | null;
  peer_evidence_aligned?: boolean | null;
  peer_grade_aligned?: boolean | null;
  peer_rationale_aligned?: boolean | null;
  peer_drift_detected?: 'Yes' | 'No' | 'Mild drift' | null;
  // Development-level-calibrated output fields (Migration 175, 2026-04-19
  // revision; column renamed by migration 246).
  development_level_context?: DevelopmentLevelContext | null;
}

interface SessionInfo {
  trainee_name: string;
  trainee_id: string;
  instructor_name: string;
  instructor_id: string;
  session_date: string;
  aircraft_type: string;
  flight_phase: string;
  session_type: string;
  session_type_obj?: { name: string; code: string } | null;
  scenario_code?: string | null;
  // Fields matching Pilot assessment (Migration 074)
  session_start_time: string | null;
  session_end_time: string | null;
  time_pf_minutes: number | null;
  time_pm_minutes: number | null;
  location: string | null;
  simulator_id: string | null;
  overall_result: string | null;
  pilot_function: string | null;
  // Development level calibration fields (Migration 175 / 239, 2026-04-19 revision).
  development_level?: string | null;
  instructor_type?: string | null;
  mpl_phase?: string | null;
  trainee_profile_notes?: string | null;
}

/**
 * IEC (Instructor and Evaluator Competency) names - authoritative ORCA360 definitions
 * Per ICAO Doc 9995, EASA AMC1 ORO.FC.231/230, EASA FCL.920, FAA AC 120-54A
 */
const CRITERION_NAMES: Record<string, string> = {
  IEC1: 'Pilot competencies',
  IEC2: 'Management of the learning environment',
  IEC3: 'Instruction',
  IEC4: 'Interaction',
  IEC5: 'Assessment and evaluation',
};

const CRITERION_DESCRIPTIONS: Record<string, string> = {
  IEC1: 'Demonstrates mastery of pilot competencies, applies TEM and CBTA/EBT methodology',
  IEC2: 'Creates positive, safe learning environment conducive to growth',
  IEC3: 'Delivers clear, structured, development-level-calibrated instruction; facilitation proportion matches the trainee development level',
  IEC4: 'Engages in effective facilitative communication and encourages self-assessment',
  IEC5: 'Provides fair, evidence-based assessment aligned with observable behaviors',
};

// Timestamp highlight duration when clicking evidence to jump to transcript
const TIMESTAMP_HIGHLIGHT_DURATION_MS = 5000;

/** Supabase may return `evidence` as null; UI always expects an array. */
function asEvidenceArray(value: unknown): Evidence[] {
  return Array.isArray(value) ? (value as Evidence[]) : [];
}

function normalizeInstructorAssessment(row: Assessment & { evidence?: unknown }): Assessment {
  return {
    ...row,
    evidence: asEvidenceArray(row.evidence),
    analysis: row.analysis ?? '',
    goals: row.goals ?? '',
  };
}

/**
 * Helper to aggregate calibration data from assessments for online view display
 * Converts boolean values to 'Yes'/'No'/'Pending' strings
 */
function getCalibrationDisplayData(assessments: Assessment[]) {
  // Find the first assessment with calibration data (all should have same values)
  const firstWithData = assessments.find(a =>
    a.ai_evidence_aligned !== null && a.ai_evidence_aligned !== undefined
  );

  const boolToDisplay = (val: boolean | null | undefined): string => {
    if (val === null || val === undefined) return 'Pending';
    return val ? 'Yes' : 'No';
  };

  const driftToDisplay = (val: 'Yes' | 'No' | 'Mild drift' | null | undefined): string => {
    if (val === null || val === undefined) return 'None';
    return val;
  };

  return {
    aiCalibration: {
      evidenceAligned: boolToDisplay(firstWithData?.ai_evidence_aligned),
      gradeSeverityAligned: boolToDisplay(firstWithData?.ai_grade_aligned),
      rationaleLogicAligned: boolToDisplay(firstWithData?.ai_rationale_aligned),
      driftDetected: driftToDisplay(firstWithData?.ai_drift_detected),
    },
    peerCalibration: {
      available: firstWithData?.peer_evidence_aligned !== null && firstWithData?.peer_evidence_aligned !== undefined,
      evidenceAligned: boolToDisplay(firstWithData?.peer_evidence_aligned),
      gradeSeverityAligned: boolToDisplay(firstWithData?.peer_grade_aligned),
      rationaleLogicAligned: boolToDisplay(firstWithData?.peer_rationale_aligned),
      driftDetected: driftToDisplay(firstWithData?.peer_drift_detected),
    },
  };
}

/**
 * Development Level Banner (2026-04-19 revision).
 *
 * Summarises the development level + instructor role used to score the
 * assessment. Falls back to the first assessment's `development_level_context` envelope
 * when the session record has null values. Shows the expected vs observed
 * facilitation balance and F3 tags as a compact read-only summary.
 */
function DevelopmentLevelBanner({
  sessionInfo,
  assessments,
}: {
  sessionInfo: SessionInfo;
  assessments: Assessment[];
}) {
  // Prefer explicit session metadata; fall back to the LLM's "assumed" values
  // stored in development_level_context (useful for legacy sessions without
  // the columns set). Both key spellings are accepted; see the interface.
  const firstDevelopmentLevelContext =
    assessments.find((a) => a.development_level_context)?.development_level_context ?? null;
  const developmentLevel =
    sessionInfo.development_level ||
    firstDevelopmentLevelContext?.traineeDevelopmentLevelAssumed ||
    firstDevelopmentLevelContext?.traineeTierAssumed ||
    'Building Competencies (default fallback)';
  const role =
    sessionInfo.instructor_type ||
    firstDevelopmentLevelContext?.instructorTypeAssumed ||
    'unknown';

  // Collect per-criterion facilitation balance + F3 tags for IEC3/IEC4.
  const iec3 = assessments.find((a) => a.criterion === 'IEC3')?.development_level_context ?? null;
  const iec4 = assessments.find((a) => a.criterion === 'IEC4')?.development_level_context ?? null;
  const balance = iec3?.facilitationBalance ?? iec4?.facilitationBalance ?? null;
  const f3 = iec3?.f3Assessment ?? iec4?.f3Assessment ?? null;
  // Dual-read: rows written before G2-05 carry the withdrawn spellings.
  const alignmentDelta = balance?.developmentLevelAlignmentDelta ?? balance?.tierAlignmentDelta;
  const alignmentVerdict = balance?.developmentLevelAlignmentVerdict ?? balance?.tierAlignmentVerdict;

  const chipColor = (state?: string) => {
    switch (state) {
      case 'observed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'partial':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'not observed':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  return (
    <section className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50/60 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div>
          <span className="font-semibold text-indigo-900">Development level:</span>{' '}
          <span className="text-indigo-800">{developmentLevel}</span>
        </div>
        <div>
          <span className="font-semibold text-indigo-900">Instructor Role:</span>{' '}
          <span className="text-indigo-800">{role}</span>
        </div>
        {sessionInfo.mpl_phase && (
          <div>
            <span className="font-semibold text-indigo-900">MPL:</span>{' '}
            <span className="text-indigo-800">{sessionInfo.mpl_phase}</span>
          </div>
        )}
        {balance && (
          <div className="flex items-center gap-2">
            <span className="font-semibold text-indigo-900">Facilitation balance:</span>
            <span className="text-indigo-800">
              observed {balance.observedFacilitationPercentage ?? balance.facilitationPercentage ?? '—'}%
              {' / expected '}
              {balance.expectedFacilitationPercentage ?? '—'}%
            </span>
            {typeof alignmentDelta === 'number' && (
              <span
                className={`px-2 py-0.5 rounded-full text-xs border ${
                  alignmentDelta <= 10
                    ? 'bg-green-100 text-green-800 border-green-200'
                    : alignmentDelta <= 25
                    ? 'bg-amber-100 text-amber-800 border-amber-200'
                    : 'bg-red-100 text-red-800 border-red-200'
                }`}
              >
                Δ {alignmentDelta}%
              </span>
            )}
            {alignmentVerdict && (
              <span className="text-xs italic text-indigo-700">
                {alignmentVerdict}
              </span>
            )}
          </div>
        )}
      </div>

      {f3 && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          <span className="font-semibold text-indigo-900">F3 (IEC-3.6):</span>
          <span className={`px-2 py-0.5 rounded-full border ${chipColor(f3.fairness)}`}>
            Fair: {f3.fairness ?? 'n/a'}
          </span>
          <span className={`px-2 py-0.5 rounded-full border ${chipColor(f3.firmness)}`}>
            Firm: {f3.firmness ?? 'n/a'}
          </span>
          <span className={`px-2 py-0.5 rounded-full border ${chipColor(f3.friendliness)}`}>
            Friendly: {f3.friendliness ?? 'n/a'}
          </span>
          {f3.comment && (
            <span className="text-indigo-700 italic">&mdash; {f3.comment}</span>
          )}
        </div>
      )}

      {sessionInfo.trainee_profile_notes && (
        <div className="mt-2 text-xs text-indigo-700">
          <span className="font-semibold">Profile:</span> {sessionInfo.trainee_profile_notes}
        </div>
      )}
    </section>
  );
}

export default function EditAssessmentInstructor() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [calibrationError, setCalibrationError] = useState<string | null>(null);
  const [selectedCriterion, setSelectedCriterion] = useState<string | null>(null);
  const sessionDraft = useSessionAssessmentDraft<Assessment>();

  // Evidence modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedEvidenceIndex, setSelectedEvidenceIndex] = useState<number | null>(null);

  // Split-screen transcript viewer state
  const [transcriptIds, setTranscriptIds] = useState<string[]>([]);
  const [activeTimestamp, setActiveTimestamp] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false); // Toggle transcript panel - hidden by default

  // V2 Assessment state (kept for additional Part A/D/E data)
  const [v2Assessment, setV2Assessment] = useState<any | null>(null);

  // Export dropdown state
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  // Edit mode — assessments load read-only; Edit button enables editing
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Version history drawer
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  const isPublished = assessments.length > 0 && assessments.every(a => a.status === 'published');

  const activeBaseline = useMemo(
    () =>
      selectedCriterion
        ? assessments.find((a) => a.criterion === selectedCriterion)
        : undefined,
    [assessments, selectedCriterion],
  );

  const activeAssessment = useMemo(
    () => (activeBaseline ? sessionDraft.getEffectiveRow(activeBaseline) : null),
    [activeBaseline, sessionDraft.getEffectiveRow, sessionDraft.dirtyCount],
  );

  // Calibration debounce timer
  const [calibrationTimer, setCalibrationTimer] = useState<NodeJS.Timeout | null>(null);

  // Self-reflection debounce timer (3-second delay, matching other auto-save fields)
  const selfReflectionTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Hooks
  const { logAction } = useAuditLog();

  // CBTA Instructor Assessment exports
  const { exportInstructorAssessmentToPDF, isExporting: isExportingCBTAPDF } = usePDFExportInstructorAssessment({
    onExportSuccess: (blob, filename) => {
      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      alert('CBTA Instructor Assessment PDF exported successfully!');
    },
    onExportError: (err) => {
      alert(`Failed to export CBTA PDF: ${err.message}`);
    },
  });

  const { exportInstructorAssessmentToWord, isExporting: isExportingCBTAWord } = useWordExportInstructorAssessment({
    onExportSuccess: (blob, filename) => {
      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      alert('CBTA Instructor Assessment Word document exported successfully!');
    },
    onExportError: (err) => {
      alert(`Failed to export CBTA Word: ${err.message}`);
    },
  });

  const loadAssessments = useCallback(async () => {
    if (!sessionId) {
      setError('No session ID provided');
      setLoading(false);
      return;
    }

    try {
      console.log('[EditAssessmentInstructor] Loading session:', sessionId);

      // Load session info (matching Pilot assessment - Migration 074 columns + Migration 175 / 239 development level fields).
      const { data: session, error: sessionError } = await supabase
        .from('sessions')
        .select(`
          id,
          session_date,
          aircraft_type,
          flight_phase,
          session_type,
          session_type_obj:session_type_id(name, code),
          scenario_code,
          session_start_time,
          session_end_time,
          time_pf_minutes,
          time_pm_minutes,
          location,
          simulator_id,
          overall_result,
          pilot_function,
          trainee_id,
          instructor_id,
          development_level,
          instructor_type,
          mpl_phase,
          trainee_profile_notes,
          trainee:trainee_id(id, full_name),
          instructor:instructor_id(id, full_name)
        `)
        .eq('id', sessionId)
        .single();

      console.log('[EditAssessmentInstructor] Session data:', session, 'Error:', sessionError);

      if (sessionError) {
        setError(`Failed to load session: ${sessionError.message} (Code: ${sessionError.code})`);
        throw sessionError;
      }

      if (!session) {
        setError('Session not found');
        return;
      }

      const traineeData = Array.isArray(session.trainee) ? session.trainee[0] : session.trainee;
      const instructorData = Array.isArray(session.instructor) ? session.instructor[0] : session.instructor;
      setSessionInfo({
        trainee_name: traineeData?.full_name || 'Unknown',
        trainee_id: traineeData?.id || 'N/A',
        instructor_name: instructorData?.full_name || 'Unknown',
        instructor_id: instructorData?.id || 'N/A',
        session_date: session.session_date,
        aircraft_type: session.aircraft_type || 'Not specified',
        flight_phase: session.flight_phase || 'Full Flight',
        session_type: session.session_type || 'Simulator',
        session_type_obj: (session as any).session_type_obj ?? null,
        scenario_code: (session as any).scenario_code ?? null,
        // Migration 074 fields (matching Pilot assessment)
        session_start_time: session.session_start_time,
        session_end_time: session.session_end_time,
        time_pf_minutes: session.time_pf_minutes,
        time_pm_minutes: session.time_pm_minutes,
        location: session.location,
        simulator_id: session.simulator_id,
        overall_result: session.overall_result,
        pilot_function: session.pilot_function,
        // Migration 175 / 239 fields (development level calibration).
        development_level: (session as any).development_level ?? null,
        instructor_type: (session as any).instructor_type ?? null,
        mpl_phase: (session as any).mpl_phase ?? null,
        trainee_profile_notes: (session as any).trainee_profile_notes ?? null,
      });

      // Load ALL transcripts for split-screen view (briefing, session(s), debriefing)
      console.log('[EditAssessmentInstructor] Loading transcripts for session:', sessionId);
      const { data: transcriptData, error: transcriptError } = await supabase
        .from('transcripts')
        .select('id, created_at')
        .eq('session_id', sessionId)
        .or('is_archived.is.null,is_archived.eq.false')
        .order('created_at', { ascending: true }); // Chronological order

      if (transcriptError) {
        console.warn('[EditAssessmentInstructor] No transcripts found:', transcriptError.message);
      } else if (transcriptData && transcriptData.length > 0) {
        const ids = transcriptData.map(t => t.id);
        console.log('[EditAssessmentInstructor] Loaded', ids.length, 'transcript(s):', ids);
        setTranscriptIds(ids);
      }

      // Load assessments
      console.log('[EditAssessmentInstructor] Loading assessments for session:', sessionId);
      const { data: assessmentData, error: assessmentError } = await supabase
        .from('assessments_instructor')
        .select('*')
        .eq('session_id', sessionId)
        .order('criterion', { ascending: true });

      console.log('[EditAssessmentInstructor] Assessment data:', assessmentData, 'Error:', assessmentError);

      if (assessmentError) {
        setError(`Failed to load assessments: ${assessmentError.message} (Code: ${assessmentError.code})`);
        console.error('[EditAssessmentInstructor] RLS Policy might be blocking access. Check if user has instructor role.');
        throw assessmentError;
      }

      if (!assessmentData || assessmentData.length === 0) {
        setError('No instructor assessments found for this session. Please generate assessments first.');
        setAssessments([]);
        return;
      }

      const normalized = assessmentData.map((row) =>
        normalizeInstructorAssessment(row as Assessment & { evidence?: unknown }),
      );
      setAssessments(normalized);
      sessionDraft.resetFromBaseline(normalized);
      setSelectedCriterion(normalized[0]!.criterion);

      console.log('[EditAssessmentInstructor] Successfully loaded', normalized.length, 'assessments');

      // Load V2 Assessment if available
      try {
        const { data: sessionData } = await supabase
          .from('sessions')
          .select('orca360_v2_instructor_assessment')
          .eq('id', sessionId)
          .single();

        if (sessionData?.orca360_v2_instructor_assessment) {
          console.log('[EditAssessmentInstructor] V2 assessment found');
          setV2Assessment(sessionData.orca360_v2_instructor_assessment);
        }
      } catch (v2Error) {
        console.log('[EditAssessmentInstructor] No V2 assessment available:', v2Error);
      }
    } catch (err: any) {
      console.error('[EditAssessmentInstructor] Error loading assessments:', err);
      if (!error) {
        setError(`Failed to load assessments: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId, error, sessionDraft.resetFromBaseline]); // Memoized with sessionId dependency

  useEffect(() => {
    loadAssessments();
  }, [loadAssessments]);

  // Auto-run calibration on page load (if calibration fields are null)
  useEffect(() => {
    const runCalibrationIfNeeded = async () => {
      if (!sessionId || assessments.length === 0) return;

      // Check if calibration has already been run
      const firstAssessment = assessments[0];
      if (
        firstAssessment &&
        firstAssessment.ai_evidence_aligned !== null &&
        firstAssessment.ai_evidence_aligned !== undefined
      ) {
        console.log('[EditAssessmentInstructor] Calibration already exists, skipping auto-run');
        return;
      }

      console.log('[EditAssessmentInstructor] Running calibration (auto-trigger on load)...');

      try {
        const response = await fetch('/api/calibrate-instructor-assessment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sessionId }),
        });

        if (!response.ok) {
          throw new Error(`Calibration failed: ${response.statusText}`);
        }

        const result = await response.json();
        console.log('[EditAssessmentInstructor] Calibration completed:', result);
        setCalibrationError(null);

        // Reload assessments to show updated calibration values
        await loadAssessments();
      } catch (err: any) {
        console.error('[EditAssessmentInstructor] Calibration error:', err);
        setCalibrationError('Auto-calibration failed. You can continue editing — calibration will retry after your next edit.');
      }
    };

    runCalibrationIfNeeded();
  }, [sessionId, assessments, loadAssessments]);

  // Debounced calibration trigger (5 seconds after last edit)
  const triggerCalibrationAfterEdit = useCallback(() => {
    if (!sessionId) return;

    // Clear existing timer
    if (calibrationTimer) {
      clearTimeout(calibrationTimer);
    }

    // Set new timer (5 seconds)
    const newTimer = setTimeout(async () => {
      console.log('[EditAssessmentInstructor] Running calibration (auto-trigger after edit)...');

      try {
        const response = await fetch('/api/calibrate-instructor-assessment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sessionId, force: true }), // force=true to recalculate after edits
        });

        if (!response.ok) {
          throw new Error(`Calibration failed: ${response.statusText}`);
        }

        const result = await response.json();
        console.log('[EditAssessmentInstructor] Calibration completed:', result);
        setCalibrationError(null);

        // Reload assessments to show updated calibration values
        await loadAssessments();
      } catch (err: any) {
        console.error('[EditAssessmentInstructor] Calibration error:', err);
        setCalibrationError('Calibration update failed. Your edits are saved — calibration will retry after your next edit.');
      }
    }, 5000); // 5 seconds

    setCalibrationTimer(newTimer);
  }, [sessionId, calibrationTimer, loadAssessments]);

  // Debounced self-reflection save (3-second delay to match other auto-save fields)
  const debouncedSaveReflection = useCallback((updatedV2: any) => {
    if (selfReflectionTimerRef.current) {
      clearTimeout(selfReflectionTimerRef.current);
    }
    selfReflectionTimerRef.current = setTimeout(async () => {
      try {
        await supabase
          .from('sessions')
          .update({ orca360_v2_instructor_assessment: updatedV2 })
          .eq('id', sessionId);
      } catch (err) {
        console.error('Error saving self-reflection:', err);
      }
    }, 3000);
  }, [sessionId]);

  // Cleanup self-reflection timer on unmount
  useEffect(() => {
    return () => {
      if (selfReflectionTimerRef.current) {
        clearTimeout(selfReflectionTimerRef.current);
      }
    };
  }, []);

  const applyBatchSaveToAssessments = useCallback(
    (batch: BatchAssessmentUpdate[], versionNumber: number): Assessment[] => {
      const dirtyIds = new Set(batch.map((b) => b.assessmentId));
      return assessments.map((row) => {
        if (!dirtyIds.has(row.id)) return row;
        const effective = sessionDraft.getEffectiveRow(row);
        return {
          ...row,
          score: effective.score,
          analysis: effective.analysis,
          goals: effective.goals,
          evidence: effective.evidence,
          version: versionNumber,
        };
      });
    },
    [assessments, sessionDraft],
  );

  // Explicit save — all dirty IEC criteria in one session version
  const handleSave = useCallback(async () => {
    if (!sessionId || !sessionDraft.isDirty()) return;
    setIsSaving(true);
    try {
      const batch = sessionDraft.buildBatchUpdates();
      if (batch.length === 0) return;

      for (const item of batch) {
        const baseline = assessments.find((a) => a.id === item.assessmentId);
        if (!baseline) continue;
        const effective = sessionDraft.getEffectiveRow(baseline);
        const changes: Record<string, { old: unknown; new: unknown }> = {};
        if (item.score !== undefined && effective.score !== baseline.score) {
          changes.score = { old: baseline.score, new: effective.score };
        }
        if (item.analysis !== undefined && effective.analysis !== baseline.analysis) {
          changes.analysis = { old: baseline.analysis, new: effective.analysis };
        }
        if (item.goals !== undefined && effective.goals !== baseline.goals) {
          changes.goals = { old: baseline.goals, new: effective.goals };
        }
        if (item.evidence !== undefined) {
          changes.evidence = { old: baseline.evidence, new: effective.evidence };
        }
        if (Object.keys(changes).length > 0) {
          await logAction({
            action: 'assessment_updated',
            entity_type: 'assessment_instructor',
            entity_id: baseline.id,
            changes,
            metadata: { session_id: sessionId, criterion: baseline.criterion },
          });
        }
      }

      const { data: { user } } = await supabase.auth.getUser();
      const versionRecord = await saveSessionBatch({
        sessionId,
        assessmentType: 'instructor',
        batchUpdates: batch,
        userId: user?.id ?? null,
        changeSummary:
          batch.length > 1
            ? `Manual edit (${batch.length} criteria) from web`
            : 'Manual edit from web',
      });

      const updated = applyBatchSaveToAssessments(batch, versionRecord.version_number);
      setAssessments(updated);
      sessionDraft.resetFromBaseline(updated);
      setIsEditMode(false);
      triggerCalibrationAfterEdit();
    } catch (err) {
      console.error('[EditAssessmentInstructor] Save error:', err);
      alert('Failed to save assessment');
    } finally {
      setIsSaving(false);
    }
  }, [
    sessionId,
    sessionDraft,
    assessments,
    logAction,
    applyBatchSaveToAssessments,
    triggerCalibrationAfterEdit,
  ]);

  const handleVersionRestore = useCallback(
    async (snapshot: Record<string, unknown>, fromVersionNumber: number) => {
      if (!sessionId) return;
      const { data: { user } } = await supabase.auth.getUser();
      const versionRecord = await createVersion(
        null,
        'instructor',
        snapshot,
        user?.id ?? null,
        {
          isAiBaseline: false,
          isRestore: true,
          sessionId,
          changeSummary: `Restored from version ${fromVersionNumber}`,
        },
      );
      if (!versionRecord) throw new Error('Failed to restore assessment version');

      await logAction({
        action: 'assessment_updated',
        entity_type: 'assessment_instructor',
        entity_id: sessionId,
        metadata: { session_id: sessionId, restored_from_version: fromVersionNumber },
      });

      const restoredRows = assessments.map((a) => {
        const criterionSnap = snapshot[a.criterion] as Record<string, unknown> | undefined;
        if (!criterionSnap) return a;
        return normalizeInstructorAssessment({
          ...a,
          score: criterionSnap.score as number,
          analysis: (criterionSnap.analysis as string) ?? a.analysis,
          goals: (criterionSnap.goals as string) ?? a.goals,
          evidence: asEvidenceArray(criterionSnap.evidence),
          version: versionRecord.version_number,
        });
      });
      setAssessments(restoredRows);
      sessionDraft.resetFromBaseline(restoredRows);
      setIsEditMode(false);
    },
    [assessments, sessionId, logAction, sessionDraft.resetFromBaseline],
  );

  const handleEdit = useCallback(() => {
    setShowExportDropdown(false);
    setIsEditMode(true);
  }, []);

  useEffect(() => {
    if (isEditMode) {
      setShowExportDropdown(false);
      setShowVersionHistory(false);
    }
  }, [isEditMode]);

  const handleCancel = useCallback(() => {
    sessionDraft.discard();
    setIsEditMode(false);
  }, [sessionDraft]);

  // Unsaved changes guard on browser close/refresh
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isEditMode && sessionDraft.isDirty()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isEditMode, sessionDraft]);

  const handleCriterionSelect = useCallback((criterion: string) => {
    if (assessments.some((a) => a.criterion === criterion)) {
      setSelectedCriterion(criterion);
    }
  }, [assessments]);

  const handleScoreChange = useCallback(
    (score: number) => {
      if (activeBaseline) {
        sessionDraft.updateRow(activeBaseline.id, { score });
      }
    },
    [activeBaseline, sessionDraft],
  );

  const handleAnalysisChange = useCallback(
    (analysis: string) => {
      if (activeBaseline) {
        sessionDraft.updateRow(activeBaseline.id, { analysis });
      }
    },
    [activeBaseline, sessionDraft],
  );

  const handleGoalsChange = useCallback(
    (goals: string) => {
      if (activeBaseline) {
        sessionDraft.updateRow(activeBaseline.id, { goals });
      }
    },
    [activeBaseline, sessionDraft],
  );

  // Click-to-jump: Click evidence timestamp to highlight in transcript
  const handleTimestampClick = (timestamp: string) => {
    console.log('[EditAssessmentInstructor] Jumping to timestamp:', timestamp);
    setActiveTimestamp(timestamp);
    // Enable transcript panel if it's hidden
    if (!showTranscript) {
      setShowTranscript(true);
    }
    // Clear active timestamp after highlight duration (fade highlight)
    setTimeout(() => {
      setActiveTimestamp(null);
    }, TIMESTAMP_HIGHLIGHT_DURATION_MS);
  };

  // Evidence management handlers
  const handleEditEvidence = (index: number) => {
    setSelectedEvidenceIndex(index);
    setIsEditModalOpen(true);
  };

  const handleBulkLLMValidation = useCallback(
    (updatedEvidence: LLMEvidence[]) => {
      if (!activeBaseline) return;
      sessionDraft.touchEvidence(activeBaseline.id, updatedEvidence as Evidence[]);
    },
    [activeBaseline, sessionDraft],
  );

  const handleSaveEvidence = (updatedEvidence: Evidence) => {
    if (!activeAssessment || !activeBaseline || selectedEvidenceIndex === null) return;

    const newEvidence = [...asEvidenceArray(activeAssessment.evidence)];
    const oldEvidence = newEvidence[selectedEvidenceIndex];
    newEvidence[selectedEvidenceIndex] = updatedEvidence;

    sessionDraft.touchEvidence(activeBaseline.id, newEvidence);

    void logAction({
      action: 'evidence_updated',
      entity_type: 'assessment_instructor',
      entity_id: activeBaseline.id,
      changes: {
        evidence: {
          old: oldEvidence,
          new: updatedEvidence,
        },
      },
      metadata: {
        session_id: sessionId,
        criterion: activeBaseline.criterion,
        evidence_index: selectedEvidenceIndex,
      },
    });
  };

  const handleDeleteEvidence = (index: number) => {
    if (!activeAssessment || !activeBaseline) return;

    const newEvidence = [...asEvidenceArray(activeAssessment.evidence)];
    const deletedEvidence = newEvidence[index];
    newEvidence.splice(index, 1);

    sessionDraft.touchEvidence(activeBaseline.id, newEvidence);

    void logAction({
      action: 'evidence_deleted',
      entity_type: 'assessment_instructor',
      entity_id: activeBaseline.id,
      changes: {
        evidence: {
          old: deletedEvidence,
          new: null,
        },
      },
      metadata: {
        session_id: sessionId,
        criterion: activeBaseline.criterion,
        evidence_index: index,
      },
    });
  };

  const handleAddEvidence = (newEvidence: Evidence) => {
    if (!activeAssessment || !activeBaseline) return;

    const updatedEvidence = [...asEvidenceArray(activeAssessment.evidence), newEvidence];
    sessionDraft.touchEvidence(activeBaseline.id, updatedEvidence);

    void logAction({
      action: 'evidence_added',
      entity_type: 'assessment_instructor',
      entity_id: activeBaseline.id,
      changes: {
        evidence: {
          old: null,
          new: newEvidence,
        },
      },
      metadata: {
        session_id: sessionId,
        criterion: activeBaseline.criterion,
      },
    });
  };

  // Keyboard shortcut for save (Ctrl+S / Cmd+S)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (isEditMode && sessionDraft.isDirty()) handleSave();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditMode, sessionDraft, handleSave]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.export-dropdown-container')) {
        setShowExportDropdown(false);
      }
    };

    if (showExportDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportDropdown]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handlePublish = async () => {
    if (isEditMode && sessionDraft.isDirty()) {
      alert('Save or cancel your current edits before publishing.');
      return;
    }
    if (!confirm('Finalize this session? Pilot + instructor assessments and the debriefing guide will be locked and the trainee will be able to view them. This cannot be undone.')) {
      return;
    }

    try {
      const { error: publishError } = await supabase.rpc('finalize_session', { p_session_id: sessionId });

      if (publishError) throw publishError;

      await logAction({
        action: 'assessment_published',
        entity_type: 'assessment_instructor',
        entity_id: sessionId || '',
        metadata: {
          session_id: sessionId,
          instructor_name: sessionInfo?.instructor_name,
        },
      });

      alert('Session finalized successfully.');
      navigate(`/sessions/${sessionId}`);
    } catch (err) {
      console.error('Error finalizing session:', err);
      // Finalisation publishes the pilot assessments too, so a migration 251
      // refusal can reach this button even though it is the instructor screen.
      alert(describeFinalisationError(err).message);
    }
  };

  // Build export data for CBTA Instructor Assessment
  const buildCBTAExportData = useCallback((): InstructorAssessmentExportData | null => {
    if (!sessionInfo || assessments.length === 0) return null;

    // Map session info to Part A (matching Pilot assessment format)
    const partA: InstructorAssessmentPartA = {
      trainee: {
        name: sessionInfo.trainee_name,
        id: sessionInfo.trainee_id,
      },
      instructor: {
        name: sessionInfo.instructor_name,
        id: sessionInfo.instructor_id,
      },
      aircraftType: sessionInfo.aircraft_type,
      sessionDate: sessionInfo.session_date
        ? formatDateForFilename(sessionInfo.session_date)
        : 'Not recorded',
      startTime: sessionInfo.session_start_time
        ? new Date(sessionInfo.session_start_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        : 'N/A',
      endTime: sessionInfo.session_end_time
        ? new Date(sessionInfo.session_end_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        : 'N/A',
      // Format time as PF/PM like Pilot assessment
      timeAsPF: {
        hours: Math.floor((sessionInfo.time_pf_minutes || 0) / 60),
        minutes: (sessionInfo.time_pf_minutes || 0) % 60,
      },
      timeAsPM: {
        hours: Math.floor((sessionInfo.time_pm_minutes || 0) / 60),
        minutes: (sessionInfo.time_pm_minutes || 0) % 60,
      },
      location: sessionInfo.location || 'Not specified',
      simulatorId: sessionInfo.simulator_id || 'N/A',
      sessionName: [
        sessionInfo.session_type_obj?.name || sessionInfo.session_type,
        sessionInfo.scenario_code,
        sessionInfo.flight_phase,
      ].filter(Boolean).join(' - ') || 'Simulator Training',
      sessionType: sessionInfo.session_type_obj?.name || sessionInfo.session_type,
      flightPhase: sessionInfo.flight_phase,
      overallResult: overallResultForExport(sessionInfo.overall_result),
      pilotFunction: ((sessionInfo.pilot_function || 'Instructor Trainee') as 'Captain' | 'First Officer' | 'Instructor Trainee' | 'Other'),
    };

    // Map assessments to the export format
    const assessmentRows: AssessmentInstructorRow[] = assessments.map(a => ({
      id: a.id,
      session_id: a.session_id,
      instructor_id: a.instructor_id,
      criterion: a.criterion,
      score: a.score,
      evidence: a.evidence.map(e => ({
        quote: e.quote,
        timestamp: e.timestamp,
        validation_status: e.validation_status as any,
        confidence: e.confidence,
        llm_decision: e.llm_decision,
        llm_reasoning: e.llm_reasoning,
      })),
      analysis: a.analysis,
      goals: a.goals,
      status: a.status === 'approved' || a.status === 'published' ? 'published' : 'draft',
      version: a.version,
      ai_generated: true,
      created_at: a.created_at,
      updated_at: a.updated_at,
      // Calibration fields (Migration 088)
      ai_evidence_aligned: a.ai_evidence_aligned,
      ai_grade_aligned: a.ai_grade_aligned,
      ai_rationale_aligned: a.ai_rationale_aligned,
      ai_drift_detected: a.ai_drift_detected,
      peer_evidence_aligned: a.peer_evidence_aligned,
      peer_grade_aligned: a.peer_grade_aligned,
      peer_rationale_aligned: a.peer_rationale_aligned,
      peer_drift_detected: a.peer_drift_detected,
    }));

    // Use V2 assessment if available
    if (v2Assessment) {
      return createInstructorAssessmentExportData(
        { id: sessionId || '', session_date: sessionInfo.session_date, aircraft_type: sessionInfo.aircraft_type, instructor: { id: '', full_name: sessionInfo.instructor_name } },
        assessmentRows,
        partA,
        v2Assessment
      );
    }

    return {
      partA,
      partB: mapAssessmentsToPartB(assessmentRows),
      partC: mapAssessmentsToPartC(assessmentRows),
      partD: mapCalibrationData(assessmentRows),
      partF: createDefaultPartF(),
      invocationLine: 'Please verify the AI assessment of your instruction (which may contain inaccuracies due to AI limitations) and comment on your path forward. Feedback on the AI is highly appreciated.',
      metadata: {
        generatedAt: new Date().toISOString(),
        version: '1.0',
        instructorId: assessments[0]?.instructor_id || '',
      },
    };
  }, [sessionInfo, assessments, sessionId, v2Assessment]);

  // CBTA PDF export handler
  const handleExportCBTAPDF = async () => {
    setShowExportDropdown(false); // Close dropdown immediately
    const exportData = buildCBTAExportData();
    if (!exportData) {
      alert('No assessment data available to export');
      return;
    }

    try {
      await exportInstructorAssessmentToPDF(exportData, sessionId);

      // Log export action
      await logAction({
        action: 'assessment_exported',
        entity_type: 'assessment_instructor',
        entity_id: sessionId || '',
        metadata: {
          session_id: sessionId,
          format: 'pdf',
          instructor_name: sessionInfo?.instructor_name,
          export_type: 'cbta_instructor_sim_assessment',
        },
      });
    } catch (err) {
      console.error('Error exporting CBTA PDF:', err);
    }
  };

  // CBTA Word export handler
  const handleExportCBTAWord = async () => {
    setShowExportDropdown(false); // Close dropdown immediately
    const exportData = buildCBTAExportData();
    if (!exportData) {
      alert('No assessment data available to export');
      return;
    }

    try {
      await exportInstructorAssessmentToWord(exportData, sessionId);

      // Log export action
      await logAction({
        action: 'assessment_exported',
        entity_type: 'assessment_instructor',
        entity_id: sessionId || '',
        metadata: {
          session_id: sessionId,
          format: 'docx',
          instructor_name: sessionInfo?.instructor_name,
          export_type: 'cbta_instructor_sim_assessment',
        },
      });
    } catch (err) {
      console.error('Error exporting CBTA Word:', err);
    }
  };

  const getValidationColor = (status: string) => {
    switch (status) {
      case 'VERIFIED':
        return 'text-green-600 bg-green-50';
      case 'VERBATIM':
        return 'text-green-600 bg-green-50'; // Green like VERIFIED
      case 'PARAPHRASED':
        return 'text-yellow-600 bg-yellow-50';
      case 'SIMILAR':
        return 'text-orange-600 bg-orange-50';
      case 'UNVERIFIED':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getValidationIcon = (status: string) => {
    switch (status) {
      case 'VERIFIED':
        return '✓';
      case 'VERBATIM':
        return '✓'; // Checkmark like VERIFIED
      case 'PARAPHRASED':
        return '⚠';
      case 'SIMILAR':
        return '⚠';
      case 'UNVERIFIED':
        return '❌';
      default:
        return '?';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orca-elevate"></div>
          <p className="mt-2 text-steel">Loading assessments...</p>
        </div>
      </div>
    );
  }

  if (error || !sessionInfo || assessments.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-semibold text-navy mb-2">No Assessments Found</h2>
          <p className="text-steel mb-6">
            {error || 'No instructor assessments have been generated for this session yet.'}
          </p>
          <button onClick={() => navigate(`/sessions/${sessionId}`)} className="btn-primary">
            Back to Session
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-white">
      {/* Compact Navigation Bar */}
      <div className="bg-navy px-4 py-2 flex items-center justify-between shrink-0 shadow-sm">
        <button
          onClick={() => navigate(`/sessions/${sessionId}`)}
          className="text-white hover:text-sky-200 text-sm font-medium flex items-center gap-2"
        >
          ← Back to Session
        </button>
        <span className="text-white/70 text-sm">
          {sessionInfo?.instructor_name} • {sessionInfo?.aircraft_type} • {sessionInfo?.session_date ? new Date(sessionInfo.session_date).toLocaleDateString() : ''}
        </span>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-auto">
        <div className="py-6 px-6">
          {/* Development Level Banner (2026-04-19 revision) */}
          <DevelopmentLevelBanner sessionInfo={sessionInfo} assessments={assessments} />

          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-navy mb-2">Edit Instructor Assessment</h1>
            <p className="text-steel">
              {sessionInfo.instructor_name} • {new Date(sessionInfo.session_date).toLocaleDateString()} • {sessionInfo.aircraft_type}
            </p>
            {/* Edit mode status */}
            {isEditMode && (
              <div className="mt-2 text-xs text-amber-600 font-medium">
                {sessionDraft.isDirty()
                  ? `Editing — ${sessionDraft.dirtyCount} unsaved change${sessionDraft.dirtyCount === 1 ? '' : 's'}`
                  : 'Editing — save or cancel when done'}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3 flex-wrap gap-y-2">
            {/* Evidence Status Indicator - Always verbatim with span-based extraction */}
            <div className="flex items-center space-x-2 px-3 py-1 rounded-md bg-green-50 border border-green-200">
              <span className="text-xs font-medium text-green-700">Evidence:</span>
              <span className="text-xs text-green-600">✓ Verbatim</span>
            </div>
            {!isPublished && (
              !isEditMode ? (
                <button
                  onClick={handleEdit}
                  disabled={!activeAssessment}
                  className="px-4 py-2 text-sm font-semibold text-white bg-orca-elevate hover:bg-orca-elevate/90 rounded-md transition-colors disabled:opacity-50"
                >
                  Edit
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !sessionDraft.isDirty()}
                    className="px-4 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50"
                    title="Save all changes (Ctrl+S / Cmd+S)"
                  >
                    {getSessionSaveButtonLabel(isSaving, sessionDraft.dirtyCount)}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-semibold text-navy bg-white border border-steel/30 hover:bg-ice rounded-md transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </>
              )
            )}
            {/* CBTA Export dropdown */}
            <div className="relative inline-block export-dropdown-container">
              <button
                onClick={() => !isEditMode && setShowExportDropdown(!showExportDropdown)}
                className="px-4 py-2 text-sm font-semibold text-navy bg-white border border-orca-main hover:bg-navy hover:text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isEditMode || isExportingCBTAPDF || isExportingCBTAWord}
                title={isEditMode ? 'Save or cancel edits before exporting' : undefined}
              >
                {isExportingCBTAPDF || isExportingCBTAWord ? 'Exporting...' : 'Export CBTA ▼'}
              </button>
              <div className={`absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-steel/20 z-10 ${showExportDropdown ? 'block' : 'hidden'}`}>
                <button
                  onClick={handleExportCBTAPDF}
                  disabled={isExportingCBTAPDF}
                  className="w-full px-4 py-2 text-sm text-left text-navy hover:bg-ice transition-colors disabled:opacity-50"
                >
                  📄 Export as PDF
                </button>
                <button
                  onClick={handleExportCBTAWord}
                  disabled={isExportingCBTAWord}
                  className="w-full px-4 py-2 text-sm text-left text-navy hover:bg-ice transition-colors disabled:opacity-50"
                >
                  📝 Export as Word
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowVersionHistory(true)}
              disabled={isEditMode || !activeAssessment}
              className="px-4 py-2 text-sm font-semibold text-navy bg-white border border-steel/30 hover:bg-ice rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title={isEditMode ? 'Save or cancel edits before opening history' : undefined}
            >
              History
            </button>
            {!isPublished && (
              <button
                onClick={handlePublish}
                disabled={isEditMode}
                className="px-4 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={isEditMode ? 'Save or cancel edits before publishing' : undefined}
              >
                Publish All
              </button>
            )}
          </div>
        </div>
      </div>

      {isEditMode && sessionDraft.isDirty() && (
        <SessionAssessmentUnsavedBanner dirtyCount={sessionDraft.dirtyCount} className="mb-4" />
      )}

      {/* View Toggle and Split-screen buttons */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowTranscript(!showTranscript)}
            className="px-4 py-2 text-sm font-semibold text-navy bg-white border border-orca-main hover:bg-navy hover:text-white rounded-md transition-colors"
          >
            {showTranscript ? '← Hide Transcript' : '→ Show Transcript'}
          </button>
          {transcriptIds.length === 0 && (
            <span className="text-sm text-steel">
              (No transcripts available for this session)
            </span>
          )}
          {transcriptIds.length > 0 && (
            <span className="text-sm text-steel">
              ({transcriptIds.length} transcript{transcriptIds.length > 1 ? 's' : ''} available)
            </span>
          )}
        </div>

      </div>

      {/* Part A: Session Identification */}
      <section className="mb-6 bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
          <h2 className="text-lg font-semibold text-slate-800">Part A — Session Identification</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="text-sm">
              <span className="text-slate-500 block">Trainee</span>
              <span className="text-slate-800 font-medium">{sessionInfo.trainee_name}</span>
            </div>
            <div className="text-sm">
              <span className="text-slate-500 block">Trainee ID</span>
              <span className="text-slate-800 font-medium">{sessionInfo.trainee_id}</span>
            </div>
            <div className="text-sm">
              <span className="text-slate-500 block">Instructor</span>
              <span className="text-slate-800 font-medium">{sessionInfo.instructor_name}</span>
            </div>
            <div className="text-sm">
              <span className="text-slate-500 block">Instructor ID</span>
              <span className="text-slate-800 font-medium">{sessionInfo.instructor_id}</span>
            </div>
            <div className="text-sm">
              <span className="text-slate-500 block">Aircraft Type</span>
              <span className="text-slate-800 font-medium">{sessionInfo.aircraft_type}</span>
            </div>
            <div className="text-sm">
              <span className="text-slate-500 block">Session Date</span>
              <span className="text-slate-800 font-medium">{new Date(sessionInfo.session_date).toLocaleDateString('en-GB')}</span>
            </div>
            <div className="text-sm">
              <span className="text-slate-500 block">Start Time</span>
              <span className="text-slate-800 font-medium">
                {sessionInfo.session_start_time
                  ? new Date(sessionInfo.session_start_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                  : 'N/A'}
              </span>
            </div>
            <div className="text-sm">
              <span className="text-slate-500 block">End Time</span>
              <span className="text-slate-800 font-medium">
                {sessionInfo.session_end_time
                  ? new Date(sessionInfo.session_end_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                  : 'N/A'}
              </span>
            </div>
            <div className="text-sm">
              <span className="text-slate-500 block">Time as PF</span>
              <span className="text-slate-800 font-medium">
                {sessionInfo.time_pf_minutes != null
                  ? `${Math.floor(sessionInfo.time_pf_minutes / 60)}h ${sessionInfo.time_pf_minutes % 60}m`
                  : 'N/A'}
              </span>
            </div>
            <div className="text-sm">
              <span className="text-slate-500 block">Time as PM</span>
              <span className="text-slate-800 font-medium">
                {sessionInfo.time_pm_minutes != null
                  ? `${Math.floor(sessionInfo.time_pm_minutes / 60)}h ${sessionInfo.time_pm_minutes % 60}m`
                  : 'N/A'}
              </span>
            </div>
            <div className="text-sm">
              <span className="text-slate-500 block">Location</span>
              <span className="text-slate-800 font-medium">{sessionInfo.location || 'N/A'}</span>
            </div>
            <div className="text-sm">
              <span className="text-slate-500 block">Simulator ID</span>
              <span className="text-slate-800 font-medium">{sessionInfo.simulator_id || 'N/A'}</span>
            </div>
            <div className="text-sm">
              <span className="text-slate-500 block">Session Type</span>
              <span className="text-slate-800 font-medium">{sessionInfo.session_type}</span>
            </div>
            <div className="text-sm">
              <span className="text-slate-500 block">Flight Phase</span>
              <span className="text-slate-800 font-medium">{sessionInfo.flight_phase}</span>
            </div>
            <div className="text-sm">
              <span className="text-slate-500 block">Function</span>
              <span className="text-slate-800 font-medium">{sessionInfo.pilot_function || 'N/A'}</span>
            </div>
            <div className="text-sm">
              <span className="text-slate-500 block">Overall Result</span>
              <span className={`font-medium ${
                sessionInfo.overall_result === 'Pass' ? 'text-green-600' :
                sessionInfo.overall_result === 'Fail' ? 'text-red-600' :
                'text-slate-800'
              }`}>{sessionInfo.overall_result || 'Incomplete'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Part C/D: Competency Assessment & Recommendations */}
      <section className="mt-6">
        <div className="px-6 py-3 bg-white rounded-t-xl shadow-sm border border-b-0 border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">Part C/D — Competency Assessment & Recommendations</h2>
        </div>
        {/* Main Assessment View */}
        <div className="flex flex-col bg-slate-50 p-6 rounded-b-xl shadow-sm border border-slate-200">
          {/* IEC pill bar — transcript visible only */}
          {showTranscript && transcriptIds.length > 0 && (
            <div className="mb-4 flex overflow-x-auto gap-1.5 pb-1 scrollbar-hide">
              {assessments.map((assessment) => {
                const display = sessionDraft.getEffectiveRow(assessment);
                const dirty = sessionDraft.rowDiffersFromBaseline(assessment);
                return (
                <button
                  key={assessment.criterion}
                  onClick={() => handleCriterionSelect(assessment.criterion)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap ${
                    selectedCriterion === assessment.criterion
                      ? 'bg-orca-elevate text-white'
                      : 'bg-steel/10 text-navy hover:bg-steel/20'
                  } ${dirty ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
                >
                  {dirty && <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-amber-400" aria-hidden />}
                  {CRITERION_NAMES[assessment.criterion]} ({assessment.criterion}) {display.score}/5
                </button>
                );
              })}
            </div>
          )}
        <div className="flex gap-6 items-stretch">
          {/* Left Side: Transcript Viewer */}
          {showTranscript && transcriptIds.length > 0 && (
            <div className="w-3/5 flex-shrink-0">
              <div className="bg-white rounded-lg shadow-md overflow-hidden sticky top-4 h-[calc(100vh-8rem)] flex flex-col">
                <TranscriptViewer
                  transcriptIds={transcriptIds}
                  sessionId={sessionId || undefined}
                  sessionInfo={{
                    trainee_name: sessionInfo?.trainee_name,
                    session_date: sessionInfo?.session_date,
                  }}
                  highlightTimestamp={activeTimestamp || undefined}
                />
              </div>
            </div>
          )}

          {/* Right panel: sidebar when transcript hidden, full editor when transcript visible */}
          <div className={`flex gap-6 ${showTranscript && transcriptIds.length > 0 ? 'w-2/5' : 'w-full'}`}>
            {/* IEC Sidebar — transcript hidden only */}
            {(!showTranscript || transcriptIds.length === 0) && (
            <div className="w-64 flex-shrink-0">
              <div className="bg-white rounded-lg shadow-md p-4">
                <h3 className="text-sm font-semibold text-navy mb-4">Instructor and Evaluator Competencies</h3>
                <div className="space-y-2">
                  {assessments.map((assessment) => {
                    const display = sessionDraft.getEffectiveRow(assessment);
                    const dirty = sessionDraft.rowDiffersFromBaseline(assessment);
                    return (
                    <button
                      key={assessment.criterion}
                      onClick={() => handleCriterionSelect(assessment.criterion)}
                      className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                        selectedCriterion === assessment.criterion
                          ? 'bg-orca-elevate text-white'
                          : 'hover:bg-ice'
                      } ${dirty && selectedCriterion !== assessment.criterion ? 'ring-1 ring-amber-400' : ''}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium flex items-center gap-1.5">
                          {dirty && (
                            <span
                              className="inline-block h-2 w-2 rounded-full bg-amber-400 shrink-0"
                              title="Unsaved changes"
                              aria-hidden
                            />
                          )}
                          {CRITERION_NAMES[assessment.criterion]}
                        </span>
                        <span className="text-xs font-semibold">
                          {display.score}/5
                        </span>
                      </div>
                      <div className="text-xs mt-1 opacity-80">
                        {assessment.criterion}
                      </div>
                    </button>
                    );
                  })}
                </div>
              </div>
            </div>
            )}

          {/* Assessment Editor */}
          {activeAssessment && (
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
              {/* Score Section */}
              <div>
                <h3 className="text-lg font-semibold text-navy mb-1">
                  {CRITERION_NAMES[activeAssessment.criterion]} ({activeAssessment.criterion})
                </h3>
                <p className="text-sm text-steel mb-3">
                  {CRITERION_DESCRIPTIONS[activeAssessment.criterion]}
                </p>
                <div className="flex items-center space-x-4">
                  <label className="text-sm font-medium text-steel">Grade:</label>
                  <div className="flex space-x-2">
                    {[1, 2, 3, 4, 5].map((score) => (
                      <button
                        key={score}
                        onClick={() => isEditMode && handleScoreChange(score)}
                        disabled={!isEditMode}
                        className={`w-10 h-10 rounded-md font-semibold transition-colors disabled:cursor-default ${
                          activeAssessment.score === score
                            ? 'bg-orca-elevate text-white'
                            : isEditMode
                            ? 'bg-steel/10 text-steel hover:bg-steel/20'
                            : 'bg-steel/10 text-steel'
                        }`}
                      >
                        {score}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Evidence Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-navy">Evidence</h4>
                  {isEditMode && (
                    <button
                      onClick={() => setIsAddModalOpen(true)}
                      className="px-3 py-1 text-xs font-semibold text-white bg-orca-elevate hover:bg-orca-elevate/90 rounded-md transition-colors"
                    >
                      + Add Evidence
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {activeAssessment.evidence && activeAssessment.evidence.length > 0 ? (
                    activeAssessment.evidence.map((item, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-md border-l-4 group hover:shadow-md transition-shadow resize-y overflow-auto min-h-[60px] max-h-[100px] ${
                          item.validation_status === 'VERIFIED' || item.validation_status === 'VERBATIM'
                            ? 'border-green-500 bg-green-50'
                            : item.validation_status === 'PARAPHRASED'
                            ? 'border-yellow-500 bg-yellow-50'
                            : item.validation_status === 'SIMILAR'
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-red-500 bg-red-50'
                        }`}
                        style={{ resize: 'vertical' }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 overflow-hidden">
                            <p className="text-sm text-navy mb-1 line-clamp-2">"{item.quote}"</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                onClick={() => handleTimestampClick(item.timestamp)}
                                className="text-xs text-orca-elevate hover:text-orca-elevate/80 hover:underline font-medium transition-colors"
                                title="Jump to transcript location"
                              >
                                @ {item.timestamp}
                              </button>
                              {/* Tier 1-3: Programmatic Validation */}
                              <span className={`px-2 py-1 text-xs font-medium rounded ${getValidationColor(item.validation_status)}`}>
                                {getValidationIcon(item.validation_status)} {item.validation_status}
                              </span>
                              {/* Tier 4: LLM Competency Mapping Validation */}
                              {item.llm_decision && (
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded border ${
                                    item.llm_decision === 'ACCEPTED'
                                      ? 'text-green-700 bg-green-50 border-green-300'
                                      : item.llm_decision === 'REJECTED'
                                      ? 'text-red-700 bg-red-50 border-red-300'
                                      : 'text-blue-700 bg-blue-50 border-blue-300'
                                  }`}
                                  title={item.llm_reasoning || 'AI validation result'}
                                >
                                  AI: {item.llm_decision === 'ACCEPTED' && '✓'}
                                  {item.llm_decision === 'REJECTED' && '✗'}
                                  {item.llm_decision === 'RECLASSIFIED' && '↻'}
                                  {' '}{item.llm_decision}
                                </span>
                              )}  
                              {item.confidence && (
                                <span className="text-xs text-steel">
                                  • Confidence: {(item.confidence * 100).toFixed(0)}%
                                </span>
                              )}                                                          
                            </div>
                          </div>
                          {isEditMode && (
                          <div className="flex items-center space-x-2 ml-3">
                            <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => handleEditEvidence(idx)}
                                className="px-2 py-1 text-xs font-semibold text-orca-elevate bg-white border border-orca-elevate hover:bg-orca-elevate hover:text-white rounded transition-colors"
                                title="Edit evidence"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm('Delete this evidence?')) {
                                    handleDeleteEvidence(idx);
                                  }
                                }}
                                className="px-2 py-1 text-xs font-semibold text-red-600 bg-white border border-red-600 hover:bg-red-600 hover:text-white rounded transition-colors"
                                title="Delete evidence"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 bg-steel/5 rounded-md border border-dashed border-steel/30">
                      <p className="text-sm text-steel mb-3">No evidence available</p>
                      {isEditMode && (
                        <button
                          onClick={() => setIsAddModalOpen(true)}
                          className="px-4 py-2 text-sm font-semibold text-white bg-orca-elevate hover:bg-orca-elevate/90 rounded-md transition-colors"
                        >
                          + Add First Evidence
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {/* Tier 4 Bulk LLM Validation */}
                <div className="mt-4">
                  <LLMValidationPanel
                    sessionId={sessionId!}
                    assessmentId={activeAssessment.id}
                    assessmentType="instructor"
                    evidence={activeAssessment.evidence}
                    onComplete={handleBulkLLMValidation}
                  />
                </div>
              </div>

              {/* Analysis Section */}
              <div>
                <h4 className="text-sm font-semibold text-navy mb-2">Development Areas Analysis</h4>
                <textarea
                  value={activeAssessment.analysis || ''}
                  onChange={(e) => handleAnalysisChange(e.target.value)}
                  readOnly={!isEditMode}
                  className={`w-full h-32 px-3 py-2 bg-white border border-steel/30 rounded-md text-sm ${isEditMode ? 'focus:outline-none focus:ring-2 focus:ring-orca-elevate' : 'cursor-default bg-steel/5'}`}
                  placeholder="Analysis of instructor development areas and teaching effectiveness..."
                />
              </div>

              {/* Professional Development Goals Section */}
              <div>
                <h4 className="text-sm font-semibold text-navy mb-2">Professional Development Goals</h4>
                <textarea
                  value={activeAssessment.goals || ''}
                  onChange={(e) => handleGoalsChange(e.target.value)}
                  readOnly={!isEditMode}
                  className={`w-full h-32 px-3 py-2 bg-white border border-steel/30 rounded-md text-sm ${isEditMode ? 'focus:outline-none focus:ring-2 focus:ring-orca-elevate' : 'cursor-default bg-steel/5'}`}
                  placeholder="Specific development goals and recommendations for instructor improvement..."
                />
              </div>

              {/* Status */}
              <div className="pt-4 border-t border-steel/20">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-steel">
                    Status: <span className="font-medium capitalize">{activeAssessment.status.replace('_', ' ')}</span>
                    {' • '}
                    Version: {activeAssessment.version}
                  </div>
                  <div className="text-xs text-steel">
                    Last updated: {new Date(activeAssessment.updated_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
          </div>
        {/* End of right side container */}
        </div>
        {/* End of main flex container */}
        </div>
      </section>

      {/* Part E: Calibration & Concordance */}
      {(() => {
        const calibrationData = getCalibrationDisplayData(assessments);
        return (
          <section className="mt-6 bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
              <h2 className="text-lg font-semibold text-slate-800">Part E — Calibration & Concordance</h2>
            </div>
            <div className="p-6">
              {calibrationError && (
                <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center justify-between">
                  <span>⚠ {calibrationError}</span>
                  <button
                    onClick={() => setCalibrationError(null)}
                    className="ml-3 text-amber-600 hover:text-amber-800"
                  >
                    ✕
                  </button>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* AI Calibration */}
                <div>
                  <h3 className="font-medium text-slate-700 mb-3">ORCA360 AI Calibration</h3>
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 text-slate-600">Evidence selection aligned with AI</td>
                        <td className={`py-2 font-medium ${calibrationData.aiCalibration.evidenceAligned === 'Yes' ? 'text-green-600' : calibrationData.aiCalibration.evidenceAligned === 'No' ? 'text-red-600' : 'text-amber-600'}`}>
                          {calibrationData.aiCalibration.evidenceAligned}
                        </td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 text-slate-600">Grade severity aligned</td>
                        <td className={`py-2 font-medium ${calibrationData.aiCalibration.gradeSeverityAligned === 'Yes' ? 'text-green-600' : calibrationData.aiCalibration.gradeSeverityAligned === 'No' ? 'text-red-600' : 'text-amber-600'}`}>
                          {calibrationData.aiCalibration.gradeSeverityAligned}
                        </td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 text-slate-600">Rationale logic aligned</td>
                        <td className={`py-2 font-medium ${calibrationData.aiCalibration.rationaleLogicAligned === 'Yes' ? 'text-green-600' : calibrationData.aiCalibration.rationaleLogicAligned === 'No' ? 'text-red-600' : 'text-amber-600'}`}>
                          {calibrationData.aiCalibration.rationaleLogicAligned}
                        </td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 text-slate-600">Drift detected</td>
                        <td className={`py-2 font-medium ${calibrationData.aiCalibration.driftDetected === 'None' ? 'text-slate-600' : calibrationData.aiCalibration.driftDetected === 'Yes' ? 'text-red-600' : 'text-amber-600'}`}>
                          {calibrationData.aiCalibration.driftDetected}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Peer Calibration */}
                <div>
                  <h3 className="font-medium text-slate-700 mb-3">Peer Calibration</h3>
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 text-slate-600">Evidence selection aligned with peers</td>
                        <td className={`py-2 font-medium ${calibrationData.peerCalibration.evidenceAligned === 'Yes' ? 'text-green-600' : calibrationData.peerCalibration.evidenceAligned === 'No' ? 'text-red-600' : 'text-amber-600'}`}>
                          {calibrationData.peerCalibration.evidenceAligned}
                        </td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 text-slate-600">Grade severity aligned</td>
                        <td className={`py-2 font-medium ${calibrationData.peerCalibration.gradeSeverityAligned === 'Yes' ? 'text-green-600' : calibrationData.peerCalibration.gradeSeverityAligned === 'No' ? 'text-red-600' : 'text-amber-600'}`}>
                          {calibrationData.peerCalibration.gradeSeverityAligned}
                        </td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 text-slate-600">Rationale logic aligned</td>
                        <td className={`py-2 font-medium ${calibrationData.peerCalibration.rationaleLogicAligned === 'Yes' ? 'text-green-600' : calibrationData.peerCalibration.rationaleLogicAligned === 'No' ? 'text-red-600' : 'text-amber-600'}`}>
                          {calibrationData.peerCalibration.rationaleLogicAligned}
                        </td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 text-slate-600">Drift detected</td>
                        <td className={`py-2 font-medium ${calibrationData.peerCalibration.driftDetected === 'None' ? 'text-slate-600' : calibrationData.peerCalibration.driftDetected === 'Yes' ? 'text-red-600' : 'text-amber-600'}`}>
                          {calibrationData.peerCalibration.driftDetected}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Part F: Instructor Self-Reflection */}
      <section className="mt-6 mb-6 bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-100 bg-indigo-50">
          <h2 className="text-lg font-semibold text-indigo-900">Part F — Instructor Self-Reflection (Mandatory)</h2>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600 italic">
            {v2Assessment?.partE?.invitationText ||
              'Please reflect on your facilitation of this session. Consider what went well, what challenges you encountered, and what you learned.'}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Facilitation Effectiveness
              </label>
              <textarea
                value={v2Assessment?.partE?.facilitationEffectiveness || ''}
                onChange={(e) => {
                  if (!isEditMode) return;
                  const updated = {
                    ...v2Assessment,
                    partE: {
                      ...v2Assessment?.partE,
                      facilitationEffectiveness: e.target.value,
                    },
                  };
                  setV2Assessment(updated);
                  debouncedSaveReflection(updated);
                }}
                readOnly={!isEditMode}
                className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${isEditMode ? 'focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500' : 'cursor-default bg-steel/5'}`}
                rows={3}
                placeholder="How effective was your facilitation approach?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Challenges Encountered
              </label>
              <textarea
                value={v2Assessment?.partE?.challenges || ''}
                onChange={(e) => {
                  if (!isEditMode) return;
                  const updated = {
                    ...v2Assessment,
                    partE: {
                      ...v2Assessment?.partE,
                      challenges: e.target.value,
                    },
                  };
                  setV2Assessment(updated);
                  debouncedSaveReflection(updated);
                }}
                readOnly={!isEditMode}
                className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${isEditMode ? 'focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500' : 'cursor-default bg-steel/5'}`}
                rows={3}
                placeholder="What challenges did you encounter during the session?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Learning Points
              </label>
              <textarea
                value={v2Assessment?.partE?.learningPoints || ''}
                onChange={(e) => {
                  if (!isEditMode) return;
                  const updated = {
                    ...v2Assessment,
                    partE: {
                      ...v2Assessment?.partE,
                      learningPoints: e.target.value,
                    },
                  };
                  setV2Assessment(updated);
                  debouncedSaveReflection(updated);
                }}
                readOnly={!isEditMode}
                className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${isEditMode ? 'focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500' : 'cursor-default bg-steel/5'}`}
                rows={3}
                placeholder="What did you learn from this session?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Path Forward
              </label>
              <textarea
                value={v2Assessment?.partE?.instructorResponse || ''}
                onChange={(e) => {
                  if (!isEditMode) return;
                  const updated = {
                    ...v2Assessment,
                    partE: {
                      ...v2Assessment?.partE,
                      instructorResponse: e.target.value,
                    },
                  };
                  setV2Assessment(updated);
                  debouncedSaveReflection(updated);
                }}
                readOnly={!isEditMode}
                className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${isEditMode ? 'focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500' : 'cursor-default bg-steel/5'}`}
                rows={3}
                placeholder="Describe your planned path forward for improvement..."
              />
            </div>
          </div>
        </div>
      </section>

      {/* Evidence Edit Modal */}
      {activeAssessment && selectedEvidenceIndex !== null && activeAssessment.evidence[selectedEvidenceIndex] && (
        <EvidenceEditModal
          evidence={activeAssessment.evidence[selectedEvidenceIndex]}
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedEvidenceIndex(null);
          }}
          onSave={handleSaveEvidence}
          onDelete={() => {
            handleDeleteEvidence(selectedEvidenceIndex);
            setIsEditModalOpen(false);
            setSelectedEvidenceIndex(null);
          }}
          sessionId={sessionId}
          assessmentId={activeAssessment.id}
          assessmentType="instructor"
        />
      )}

      {/* Add Evidence Modal */}
      <AddEvidenceModal
        sessionId={sessionId || ''}
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddEvidence}
      />

      {/* Version History Drawer */}
      <VersionHistoryDrawer
        assessmentId={activeAssessment?.id}
        sessionId={sessionId}
        assessmentType="instructor"
        isOpen={showVersionHistory}
        onClose={() => setShowVersionHistory(false)}
        onRestore={handleVersionRestore}
      />
        </div>
      </div>
    </div>
  );
}
